'use client';

import { useState, useEffect, useCallback, useRef, type RefObject } from 'react';

type PortalPosition = {
  top: number;
  left: number;
  width: number;
  maxHeight: number;
};

type UsePortalPositionOptions = {
  /** Whether the portal overlay is currently open */
  isOpen: boolean;
  /** Ref to the trigger element (button, input container) */
  triggerRef: RefObject<HTMLElement | null>;
  /** Ref to the portaled popover element (dropdown menu, calendar) */
  popoverRef: RefObject<HTMLElement | null>;
  /** Estimated height of the popover for flip detection (default: 250) */
  estimatedHeight?: number;
  /** Gap between trigger and popover in px (default: 12) */
  offset?: number;
  /** Padding from viewport edges in px (default: 16) */
  padding?: number;
  /** Minimum width for the popover — useful when popover should be wider than trigger */
  minWidth?: number;
  /** Max height constraints for scrollable popovers (e.g., dropdown menus) */
  maxHeightConstraint?: { min: number; max: number };
  /** Called when user scrolls outside the popover — typically used to close it */
  onClose: () => void;
};

type UsePortalPositionResult = {
  /** Calculated fixed position for the portaled element */
  position: PortalPosition;
  /** Whether the popover should render above the trigger */
  shouldOpenAbove: boolean;
};

/**
 * Hook for positioning portal-rendered overlays (dropdowns, date pickers, etc.)
 * relative to a trigger element using `position: fixed`.
 *
 * Handles:
 * - Position calculation from trigger's `getBoundingClientRect()`
 * - Flip-above detection when insufficient space below
 * - Max height calculation based on available viewport space
 * - Scroll-outside-to-close (allows scrolling within the popover)
 * - Resize recalculation
 * - Cleanup on unmount / close
 *
 * @component
 * @param options - Configuration for portal positioning
 * @returns Position data and flip state
 *
 * @example
 * ```tsx
 * const { position, shouldOpenAbove } = usePortalPosition({
 *   isOpen,
 *   triggerRef: buttonRef,
 *   popoverRef: menuRef,
 *   estimatedHeight: 250,
 *   offset: 12,
 *   maxHeightConstraint: { min: 200, max: 445 },
 *   onClose: () => setIsOpen(false),
 * });
 *
 * {isOpen && createPortal(
 *   <ul ref={menuRef} style={{
 *     position: 'fixed',
 *     top: shouldOpenAbove ? 'auto' : `${position.top}px`,
 *     bottom: shouldOpenAbove ? `${window.innerHeight - position.top}px` : 'auto',
 *     left: `${position.left}px`,
 *     width: `${position.width}px`,
 *     maxHeight: `${position.maxHeight}px`,
 *   }}>
 *     ...
 *   </ul>,
 *   document.body
 * )}
 * ```
 */
const usePortalPosition = ({
  isOpen,
  triggerRef,
  popoverRef,
  estimatedHeight = 250,
  offset = 12,
  padding = 16,
  minWidth,
  maxHeightConstraint,
  onClose,
}: UsePortalPositionOptions): UsePortalPositionResult => {
  const [shouldOpenAbove, setShouldOpenAbove] = useState(false);
  const [position, setPosition] = useState<PortalPosition>({
    top: 0,
    left: 0,
    width: 0,
    maxHeight: maxHeightConstraint?.max ?? 9999,
  });

  // Store onClose in a ref to avoid re-attaching listeners when callback changes
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Extract primitive values from maxHeightConstraint to avoid object reference in deps
  const maxHeightMin = maxHeightConstraint?.min;
  const maxHeightMax = maxHeightConstraint?.max;

  // Store triggerRef in a stable ref to keep it out of useCallback deps
  const triggerRefStable = useRef(triggerRef);
  triggerRefStable.current = triggerRef;

  const calculatePosition = useCallback(() => {
    const triggerRect = triggerRefStable.current.current?.getBoundingClientRect();
    if (!triggerRect) return;

    const spaceBelow = window.innerHeight - triggerRect.bottom;
    const spaceAbove = triggerRect.top;

    const openAbove = spaceBelow < estimatedHeight + padding && spaceAbove > estimatedHeight;
    setShouldOpenAbove(openAbove);

    // Calculate max height from available viewport space
    const availableSpace = openAbove ? spaceAbove - padding : spaceBelow - padding;
    const maxHeight =
      maxHeightMin != null && maxHeightMax != null
        ? Math.min(maxHeightMax, Math.max(maxHeightMin, availableSpace))
        : availableSpace;

    const width = minWidth ? Math.max(triggerRect.width, minWidth) : triggerRect.width;
    // Clamp left so a popover wider than its trigger (e.g. minWidth) never overflows
    // past the right edge of the viewport — the hook otherwise only flips vertically.
    const maxLeft = Math.max(window.innerWidth - width - padding, padding);
    const left = Math.min(Math.max(triggerRect.left, padding), maxLeft);

    setPosition({
      top: openAbove ? triggerRect.top - offset : triggerRect.bottom + offset,
      left,
      width,
      maxHeight,
    });
  }, [estimatedHeight, offset, padding, minWidth, maxHeightMin, maxHeightMax]);

  useEffect(() => {
    if (!isOpen || !triggerRef.current) return;

    calculatePosition();

    const handleScroll = (e: Event) => {
      const target = e.target as HTMLElement;
      const popover = popoverRef.current;

      // Allow scrolling within the popover itself
      if (popover && (target === popover || popover.contains(target))) {
        return;
      }

      // Close when scrolling outside
      onCloseRef.current();
    };

    const handleResize = () => {
      calculatePosition();
    };

    document.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleResize);

    return () => {
      document.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refs are stable, onClose accessed via ref
  }, [isOpen, calculatePosition]);

  return { position, shouldOpenAbove };
};

export default usePortalPosition;
